import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { solairSystemsScripts, SOLAIR_SYSTEM_FAQS } from "@/lib/solairJsonLd";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { ExteriorVerticalShadesBody } from "@/components/ExteriorVerticalShadesBody";
import hero from "@/assets/solair-pro-exterior-vertical-shade-patio.jpg";





export const Route = createFileRoute("/exterior-vertical-shades")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Solair® Exterior Vertical Shades & Motorized Drop Screens | San Antonio & Austin",
      description:
        "Authorized Solair® dealer for exterior vertical shades & motorized drop screens. Side-track screens for San Antonio & Austin patios. Sunbrella & Phifer or Textilene mesh, Somfy motors.",
      image: hero,
      imageAlt: "Solair® exterior vertical drop screen lowered over a Texas patio",
      socialTitle: "Solair® Exterior Vertical Shades & Motorized Drop Screens — Texas Dealer",
      socialDescription:
        "Solair® motorized exterior drop screens with side tracks — block sun, wind & bugs. Installed across San Antonio, Austin & the Hill Country by Texas Made Shade.",
      keywords:
        "Solair®, Solair® exterior vertical shades, Solair® drop screens, exterior vertical shades, motorized drop screens San Antonio, motorized drop screens Austin TX, motorized outdoor shades with side tracks, motorized patio shades Austin, motorized shades San Antonio, drop screens Texas, motorized screens Austin, exterior solar screens San Antonio, side-track patio screens, retractable patio screens Texas, Solair® drop screens dealer, automatic patio shade, smart shades Austin TX, phantom screens alternative, motorized porch screens Austin, Sunbrella drop screens, Phifer or Textilene motorized screens, screened patio San Antonio, screened porch Austin, zip track screens Texas, cable guided vertical curtain, vertical curtain patio screen, patio wind screens Texas, outdoor roller shades San Antonio, motorized patio enclosure screens, solar screen shades for patios, Somfy motorized screens Texas, wind sensor patio screens, exterior shade screens New Braunfels, drop shades Boerne TX, patio screens Seguin TX, motorized shades Kerrville, outdoor shades Fredericksburg TX, motorized shades San Marcos TX, west facing patio sun blocking screens, bug screens for patio Texas, authorized Solair dealer Texas, commercial patio drop screens, restaurant patio wind screens San Antonio, Texas Made Shade"
    }).concat([
      { name: "news_keywords", content: "Solair exterior vertical shades, motorized drop screens, San Antonio patio screens, Austin motorized shades" },
      { name: "subject", content: "Solair® exterior vertical shades and motorized drop screens in Central & South Texas" },
      { name: "coverage", content: "San Antonio, Austin, Seguin, New Braunfels, Boerne, Kerrville, Fredericksburg, San Marcos, Texas" },
      { name: "geo.region", content: "US-TX" },
      { name: "geo.placename", content: "San Antonio, Austin, Seguin, New Braunfels, Boerne, Houston, Dallas, Fort Worth, Corpus Christi, Texas" },
      { name: "rating", content: "general" },
      { name: "distribution", content: "global" },
      { name: "revisit-after", content: "7 days" },
    ]),
    scripts: [
      ...serviceHeadScripts({
      path: "/exterior-vertical-shades",
      serviceName: "Solair® Exterior Vertical Shades & Motorized Drop Screens",
      breadcrumbName: "Exterior Vertical Shades",
      description:
        "Authorized Solair® exterior vertical shade dealer for Texas. Motorized drop screens with side tracks, Sunbrella & Phifer or Textilene mesh, Somfy motors and wind sensors — designed and installed across San Antonio, Austin and the Hill Country.",
      faqs: [
        {
          q: "What is a Solair® exterior vertical shade?",
          a: "A Solair® exterior vertical shade (also called a motorized drop screen) is a powered roller screen that drops vertically from a cassette mounted above your patio opening. Side tracks (zip tracks or cable tracks) hold the fabric taut against wind, blocking sun, glare, bugs and rain spray while keeping your view.",
        },
        {
          q: "What's the difference between this and a retractable awning?",
          a: "A retractable awning extends horizontally overhead for shade from above. An exterior vertical shade drops straight down to block low-angle sun, wind and visibility through an opening — perfect for west-facing San Antonio and Austin patios, lanais and screened porches.",
        },
        {
          q: "Do exterior vertical shades block wind and bugs?",
          a: "Yes. With side tracks installed, Phifer® or Textilene® mesh screens block bugs and cut wind dramatically while still allowing breeze and view-through. Solid Sunbrella drop screens give full privacy and weather protection.",
        },
        {
          q: "Are they motorized? Can I use a smart-home app?",
          a: "Yes. Solair® drop screens ship standard with a 120V Somfy® tubular motor and handheld remote. Options include wall switches, sun/wind sensors, Alexa, Google Home and Apple Home integration for fully automatic operation.",
        },
        {
          q: "How big can a single drop screen be?",
          a: "Solair® exterior vertical shades cover openings up to roughly 20 ft wide and 12 ft tall per unit. Wider patios are covered with side-by-side units controlled together.",
        },
        {
          q: "Do you install these in San Antonio and Austin?",
          a: "Yes — Texas Made Shade installs Solair® exterior vertical shades across San Antonio, Austin, New Braunfels, Boerne, Kerrville, Fredericksburg, San Marcos and the broader Hill Country from our Seguin, TX fabrication shop.",
        },
        {
          q: "How is this different from Phantom Screens?",
          a: "Phantom Screens is a similar motorized retractable screen system. Solair® gives you a wider Sunbrella® and Phifer® or Textilene® fabric palette, a heavier-duty extruded cassette and is supported by a local Texas dealer (us) instead of a 1-800 number. We service everything we sell.",
        },
        ...SOLAIR_SYSTEM_FAQS,
      ],
      }),
      ...solairSystemsScripts(),
    ],
  }),
  component: ExteriorVerticalShadesPage,
});

function ExteriorVerticalShadesPage() {
  return (
    <div className="min-h-screen bg-cream text-navy-deep">
      <SiteHeader />
      <ExteriorVerticalShadesBody />
      <SiteFooter />
    </div>
  );
}

